import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { chatApi } from '../api';
import { useToast } from '../context/ToastContext';

/**
 * One analysis thread: messages, the in-flight streaming buffer, and send/stop.
 *
 * Streaming is the default path. The optimistic user message appears immediately,
 * tokens accumulate into `streamText`, and on `done` the real persisted message
 * (with parsed attributes) replaces the buffer — so the attribute panel appears
 * exactly once, when it is real, rather than flickering as tokens arrive.
 */
export function useChat({ onConversationCreated } = {}) {
  const toast = useToast();
  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [streaming, setStreaming] = useState(false);
  const [streamText, setStreamText] = useState('');
  const [pending, setPending] = useState(null);
  const [error, setError] = useState(null);
  const [images, setImages] = useState([]);
  const [target, setTarget] = useState(null);
  const [resolvedTarget, setResolvedTarget] = useState(null);
  const abortRef = useRef(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      abortRef.current?.abort();
    };
  }, []);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    setConversationId(null);
    setMessages([]);
    setStreamText('');
    setPending(null);
    setStreaming(false);
    setError(null);
    setImages([]);
    setTarget(null);
    setResolvedTarget(null);
  }, []);

  const loadConversation = useCallback(
    async (id) => {
      abortRef.current?.abort();
      setStreaming(false);
      setStreamText('');
      setPending(null);
      setError(null);
      // Cleared, not carried: image 2 of the thread being left is a different
      // photograph from image 2 of the thread being opened.
      setTarget(null);
      setResolvedTarget(null);
      try {
        const conversation = await chatApi.getConversation(id);
        if (!mounted.current) return null;
        setConversationId(conversation.id);
        setMessages(conversation.messages || []);
        setImages(conversation.images || []);
        return conversation;
      } catch (cause) {
        toast.error(cause, { title: 'Could not open that conversation' });
        return null;
      }
    },
    [toast],
  );

  const stop = useCallback(() => {
    abortRef.current?.abort();
    setStreaming(false);
    setStreamText('');
    setPending(null);
  }, []);

  const send = useCallback(
    async (request, { stream = true } = {}) => {
      // `displayText` and `previewImages` are for the optimistic bubble only. The
      // API rejects unknown fields (deliberately — a misspelled field should be a
      // 422, not a silent default), so they must not reach the request body.
      const { displayText, previewImages, ...wire } = request;
      const payload = { conversation_id: conversationId, ...wire };

      // Applied here rather than at every call site, so a new caller cannot forget
      // it and quietly get the last-discussed image instead of the chosen one.
      // Skipped when the request brings its own images: that is a new analysis, not
      // a question about an earlier picture.
      if (target && !payload.target_image_id && !wire.image_ids?.length) {
        payload.target_image_id = target;
      }

      setError(null);
      setStreamText('');
      setPending({
        role: 'user',
        id: `pending-${Date.now()}`,
        content: displayText || wire.prompt_text || 'Analysing…',
        images: previewImages || [],
        pending: true,
      });

      if (!stream) {
        setStreaming(true);
        try {
          const result = await chatApi.send(payload);
          if (!mounted.current) return null;
          setConversationId(result.conversation.id);
          // Several images are coded separately, so one send can return several
          // turn pairs. Interleaved into question/answer order — appending all the
          // questions and then all the answers would read as a different exchange
          // from the one that happened.
          const questions = result.user_messages || [result.user_message];
          const answers = result.messages || [result.message];
          const appended = questions.flatMap((question, index) =>
            [question, answers[index]].filter(Boolean),
          );
          setMessages((current) => [...current, ...appended]);
          setImages(result.images || result.conversation?.images || []);
          setResolvedTarget(result.resolved_target || null);
          if (!conversationId) onConversationCreated?.(result.conversation);
          return result;
        } catch (cause) {
          setError(cause);
          toast.error(cause);
          return null;
        } finally {
          if (mounted.current) {
            setStreaming(false);
            setPending(null);
          }
        }
      }

      const controller = new AbortController();
      abortRef.current = controller;
      setStreaming(true);
      let buffered = '';

      try {
        await chatApi.stream(payload, {
          signal: controller.signal,
          onEvent: (name, data) => {
            if (!mounted.current) return;
            if (name === 'start') {
              // Reset per cycle, not per request: several images produce several
              // start/token/done cycles in one response, and without this the
              // second image's tokens would be appended to the first image's text.
              buffered = '';
              setStreamText('');
              setConversationId(data.conversation_id);
              setPending(null);
              setMessages((current) => [...current, data.user_message]);
              if (data.images) setImages(data.images);
              if (data.resolved_target) setResolvedTarget(data.resolved_target);
              if (!conversationId) onConversationCreated?.({ id: data.conversation_id });
            } else if (name === 'token') {
              buffered += data.text || '';
              setStreamText(buffered);
            } else if (name === 'done') {
              setMessages((current) => [...current, data.message]);
              setStreamText('');
            } else if (name === 'error') {
              const failure = Object.assign(new Error(data.message || 'Generation failed'), {
                code: data.code,
                details: data.details,
              });
              setError(failure);
              toast.error(failure);
            }
          },
        });
        return true;
      } catch (cause) {
        if (cause?.name === 'AbortError') return null;
        setError(cause);
        toast.error(cause);
        return null;
      } finally {
        if (mounted.current) {
          setStreaming(false);
          setStreamText('');
          setPending(null);
        }
        abortRef.current = null;
      }
    },
    // `target` is load-bearing here, not incidental. Without it in the dependency
    // list `send` closes over the value from the render that created it — which is
    // `null` — so selecting an image changed the chips and sent nothing, and every
    // follow-up silently fell back to the last-discussed image. The symptom is an
    // answer about the wrong photograph that reads exactly like a correct one.
    [conversationId, onConversationCreated, target, toast],
  );

  /** The newest structured extraction in the thread — what the inspector shows. */
  const attributes = useMemo(() => {
    for (let index = messages.length - 1; index >= 0; index -= 1) {
      const message = messages[index];
      if (message.role === 'assistant' && message.attributes) return message.attributes;
    }
    return null;
  }, [messages]);

  const groundingImages = useMemo(() => {
    const first = messages.find((message) => message.role === 'user' && message.images?.length);
    return first?.images || [];
  }, [messages]);

  const lastAssistant = useMemo(
    () => [...messages].reverse().find((message) => message.role === 'assistant') || null,
    [messages],
  );

  return {
    conversationId,
    messages,
    pending,
    streaming,
    streamText,
    error,
    attributes,
    groundingImages,
    lastAssistant,
    hasImage: groundingImages.length > 0,
    /** The thread's numbered images: `[{index, image_id, filename, label}]`. */
    images,
    /** The image the next question is aimed at, or null to follow the thread. */
    target,
    setTarget,
    /** What the server did with the last question — see ResolvedTarget. */
    resolvedTarget,
    send,
    stop,
    reset,
    loadConversation,
  };
}
